/* eslint-disable max-len */
import { Alert, Card, Col, Pagination, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useProfily from 'hooks/useProfily';

import { IDokumentyJinaPrehled, IDokumentyJinyPrikaz, dokumentJinaPrehled } from 'services/dokumenty/prikazy';
import {
    IDokument,
    IDokumentyPrehled,
    IDokumentyPrehledInput,
    dokumentDownload,
    dokumentIsinApi,
    dokumentPrehledApi,
    dokumentVyjadreniApi,
    dokumentZmenaStavuApi
} from 'services/dokumenty/smlouva';

import { EPodTypDokumentu, EStavZadostiDok, ETypDokumentu } from 'enums/dokumentyEnums';
import { ENotificationType } from 'enums/notificationType';
import { EProfilyTyp } from 'enums/profilyTyp';
import routePathEnum from 'enums/routePathEnum';

import getLanguageObject from 'utils/getLanguageObject';

interface IPrehledEmitent {}

const PrehledEmitent: React.FC<IPrehledEmitent> = () => {
    //  const { navigateWorkaround } = useNavigateWorkaround();
    const { aktivniFirma, NotificationComponent, menuProfile, dokumenty, language } = useContext(Context);
    const { setLevel } = useProfily();
    const [prehled, setPrehled] = useState<IDokumentyPrehled | IDokumentyJinaPrehled | undefined>();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<{ isin: boolean; prehled: boolean; vyjadreni: boolean; soubor: boolean }>({
        isin: !!menuProfile?.smlouvydodatky && !menuProfile.neplatneaml,
        prehled: !!menuProfile?.smlouvydodatky && !menuProfile.neplatneaml,
        vyjadreni: false,
        soubor: false
    });
    const [pagination, setPagination] = useState<IDokumentyPrehledInput>({
        order: 'zmenadatum:desc',
        limit: 10,
        offset: 0,
        aktivni: true
    });
    const [hasIsin, setHasIsin] = useState<boolean>(false);
    const [modalVyjadreni, setModalVyjadreni] = useState<string>('');

    useEffect(() => {
        if (aktivniFirma?.typ !== EProfilyTyp.JINAOSOBA) {
            if (menuProfile?.smlouvydodatky && !menuProfile.neplatneaml) {
                setLoading(pv => {
                    return {
                        ...pv,
                        isin: true,
                        prehled: true
                    };
                });
                setErrStatus(0);
                Promise.allSettled([
                    dokumentIsinApi()
                        .then(resp => {
                            setLevel('dokumenty', {
                                ...dokumenty,
                                dokumentyPrava: resp,
                                dokumentyMenuReady: true
                            });
                            !resp.isin && setHasIsin(true);
                        })
                        .finally(() =>
                            setLoading(pv => {
                                return {
                                    ...pv,
                                    isin: false
                                };
                            })
                        ),
                    dokumentPrehledApi(pagination)
                        .then(resp => {
                            setPrehled(resp);
                        })
                        .catch((err: any) => {
                            setPrehled(undefined);
                            setErrStatus(err.response?.status);
                        })
                        .finally(() =>
                            setLoading(pv => {
                                return {
                                    ...pv,
                                    prehled: false
                                };
                            })
                        )
                ]);
            }
        } else {
            setLoading(pv => {
                return {
                    ...pv,
                    prehled: true
                };
            });
            dokumentJinaPrehled(pagination)
                .then(resp => {
                    setPrehled(resp);
                })
                .catch((err: any) => {
                    setPrehled(undefined);
                    setErrStatus(err.response?.status);
                })
                .finally(() =>
                    setLoading(pv => {
                        return {
                            ...pv,
                            prehled: false
                        };
                    })
                );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination]);

    return (
        <LoadingWrapper loading={loading.isin || loading.prehled}>
            <Title level={3} text="DOKUMENTY" />
            {!loading.isin && !loading.prehled && (
                <>
                    {errStatus && errStatus !== 404 ? (
                        <Row
                            justify="center"
                            align="middle"
                            style={{
                                height: '100%'
                            }}
                        >
                            <Col span={16} flex={'0 0 auto'}>
                                <Title text="SLUZBA_NEDOSTUPNA" level={3} />
                            </Col>
                        </Row>
                    ) : menuProfile?.neplatneaml ? (
                        <Alert
                            message={
                                <TranslateText>
                                    {menuProfile.datplatamlz ? 'NEPLATNE_AML_ERR' : 'NEPLATNE_AML_ERR_NODATE'}
                                </TranslateText>
                            }
                            type="error"
                            style={{
                                marginBottom: 20
                            }}
                        />
                    ) : !menuProfile?.smlouvydodatky && aktivniFirma?.typ !== 'JINAOSOBA' ? (
                        <Paragraph
                            style={{
                                display: 'block',
                                maxWidth: 800,
                                marginBottom: 20
                            }}
                            text="DOKUMENTY_NENI_PRAVO"
                        />
                    ) : !hasIsin && aktivniFirma?.typ !== 'JINAOSOBA' ? (
                        <Paragraph
                            style={{
                                display: 'block',
                                maxWidth: 800,
                                marginBottom: 20
                            }}
                            text="DOKUMENTY_NENI_ISIN"
                        />
                    ) : dokumenty?.dokumentyPrava?.smlouva && aktivniFirma?.typ !== 'JINAOSOBA' ? (
                        <Paragraph
                            style={{
                                display: 'block',
                                maxWidth: 800,
                                marginBottom: 20
                            }}
                            text="DOKUMENTY_NENI_SMLOUVA"
                        />
                    ) : prehled?.smlouvy?.length ? (
                        prehled.smlouvy.map((smlouva, i) => (
                            <Row key={i}>
                                <Paragraph
                                    style={{
                                        display: 'block',
                                        maxWidth: 800,
                                        marginBottom: 20,
                                        fontWeight: 'bold'
                                    }}
                                    text={
                                        smlouva.podtyp === EPodTypDokumentu.SMLOUVA
                                            ? smlouva.elverze
                                                ? 'DOKUMENTY_PREHLED_SMLOUVA_EL'
                                                : 'DOKUMENTY_PREHLED_SMLOUVA'
                                            : smlouva.elverze
                                              ? 'DOKUMENTY_PREHLED_SMLOUVADLT_EL'
                                              : 'DOKUMENTY_PREHLED_SMLOUVADLT'
                                    }
                                />
                                {smlouva.elverze && smlouva.nazevsouboru && (
                                    <Row>
                                        <ButtonComponent
                                            onClick={() => {
                                                setLoading(pv => {
                                                    return {
                                                        ...pv,
                                                        soubor: true
                                                    };
                                                });
                                                dokumentDownload(smlouva.iddok, smlouva.nazevsouboru).finally(() =>
                                                    setLoading(pv => {
                                                        return {
                                                            ...pv,
                                                            soubor: false
                                                        };
                                                    })
                                                );
                                            }}
                                            loading={loading.soubor}
                                            buttontext="STAHNOUT_PDF"
                                        />
                                    </Row>
                                )}
                            </Row>
                        ))
                    ) : (
                        <></>
                    )}
                    {prehled?.dokumenty.length ? (
                        <>
                            <Row gutter={16}>
                                {(prehled.dokumenty as any)
                                    ?.filter(
                                        (e: any) =>
                                            e.stav !== EStavZadostiDok.ZAMITNUTO &&
                                            e.stav !== EStavZadostiDok.ZPRACOVANO &&
                                            e.stav !== EStavZadostiDok.ZRUSENO
                                    )
                                    .map((dokument: IDokument | IDokumentyJinyPrikaz, i: number) => {
                                        return (
                                            <Col
                                                key={dokument.iddok}
                                                span={12}
                                                style={{
                                                    marginBottom: 16
                                                }}
                                            >
                                                <Card
                                                    key={i}
                                                    className="dash-card"
                                                    title={`${
                                                        getLanguageObject(language)[
                                                            dokument.typ === ETypDokumentu.PRIKAZY
                                                                ? 'DOKUMENTY_PRIKAZ_' + dokument.podtyp
                                                                : dokument.podtyp
                                                                  ? 'TYP_SMLOUVY_' + dokument.podtyp
                                                                  : 'NOVY_DOKUMNET'
                                                        ]
                                                    }${
                                                        dokument.druhcp
                                                            ? ' (' +
                                                              getLanguageObject(language)[
                                                                  'ISIN_EMISE_DRUH_' + dokument.druhcp
                                                              ] +
                                                              ')'
                                                            : ''
                                                    }`}
                                                >
                                                    {dokument.isin && (
                                                        <>
                                                            <Title text="ISIN" level={5} />
                                                            <p>{dokument.isin}</p>
                                                        </>
                                                    )}
                                                    {(dokument as IDokumentyJinyPrikaz).idmb && (
                                                        <>
                                                            <Title text="JINA_OSOBA_ICO" level={5} />
                                                            <p>{(dokument as IDokumentyJinyPrikaz).idmb}</p>
                                                        </>
                                                    )}
                                                    {(dokument as IDokumentyJinyPrikaz).emitent && (
                                                        <>
                                                            <Title text="JINA_OSOBA_NAZEV_SPOLECNOSTI" level={5} />
                                                            <p>{(dokument as IDokumentyJinyPrikaz).emitent}</p>
                                                        </>
                                                    )}
                                                    <Title text="AMLZ_PREHLED_DATUM" level={5} />
                                                    <p>{dayjs(dokument?.zmenadatum).format('DD.MM.YYYY')}</p>
                                                    <Title text="EMP_PODANE_ZADOSTI_STAV" level={5} />
                                                    <p>
                                                        <TranslateText>{`DOKUMENTY_STAV_${dokument.stav}`}</TranslateText>
                                                    </p>
                                                    <div
                                                        style={{
                                                            paddingBottom: 30,
                                                            display: 'block'
                                                        }}
                                                    />
                                                </Card>
                                                <Row
                                                    className="button-row"
                                                    style={{
                                                        position: 'absolute',
                                                        left: 30,
                                                        bottom: 10
                                                    }}
                                                >
                                                    {(dokument.stav === EStavZadostiDok.ROZP ||
                                                        dokument.stav === EStavZadostiDok.DOPL ||
                                                        dokument.stav === EStavZadostiDok.ZKONTROLOVANO ||
                                                        dokument.stav === EStavZadostiDok.KDOPLNENI) && (
                                                        <>
                                                            <AnchorComponent
                                                                link={true}
                                                                to={
                                                                    aktivniFirma
                                                                        ? `/firma/${aktivniFirma?.iducet}${routePathEnum.DOKUMENTY_NOVY}`
                                                                        : ''
                                                                }
                                                                onClick={() =>
                                                                    setLevel('dokumenty', {
                                                                        ...dokumenty,
                                                                        selectedZadost: dokument,
                                                                        prehledData: prehled
                                                                    })
                                                                }
                                                            >
                                                                <ButtonComponent
                                                                    buttontext={'POKRACOVAT'}
                                                                    id="emitentPrehledPokracovatBtn"
                                                                />
                                                            </AnchorComponent>
                                                            <ButtonComponent
                                                                id="emitentPrehledZrusitZadostBtn"
                                                                buttontext={'ZRUSIT_DOKUMENT'}
                                                                loading={loading.vyjadreni}
                                                                onClick={() => {
                                                                    setLoading(pv => {
                                                                        return {
                                                                            ...pv,
                                                                            vyjadreni: true
                                                                        };
                                                                    });
                                                                    dokumentZmenaStavuApi(
                                                                        dokument.iddok,
                                                                        EStavZadostiDok.ZRUSENO,
                                                                        aktivniFirma?.typ === EProfilyTyp.JINAOSOBA
                                                                    )
                                                                        .then(() => {
                                                                            NotificationComponent({
                                                                                title: 'ZRUSIT_DOKUMENT',
                                                                                description: 'ZRUSIT_ZADOST_SUCCESS',
                                                                                type: ENotificationType.SUCCESS
                                                                            });
                                                                        })
                                                                        .finally(() => {
                                                                            setLoading(pv => {
                                                                                return {
                                                                                    ...pv,
                                                                                    vyjadreni: false
                                                                                };
                                                                            });
                                                                            setPagination(pv => {
                                                                                return {
                                                                                    ...pv
                                                                                };
                                                                            });
                                                                        });
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                    {(dokument.stav === EStavZadostiDok.DOPL ||
                                                        dokument.stav === EStavZadostiDok.KDOPLNENI) && (
                                                        <ButtonComponent
                                                            loading={loading.vyjadreni}
                                                            buttontext={'VYJADRENI_CDCP'}
                                                            onClick={() => {
                                                                setLoading(pv => {
                                                                    return {
                                                                        ...pv,
                                                                        vyjadreni: true
                                                                    };
                                                                });
                                                                dokumentVyjadreniApi(
                                                                    dokument.iddok,
                                                                    aktivniFirma?.typ === EProfilyTyp.JINAOSOBA
                                                                )
                                                                    .then(data => setModalVyjadreni(data.vyjadreni))
                                                                    .finally(() =>
                                                                        setLoading(pv => {
                                                                            return {
                                                                                ...pv,
                                                                                vyjadreni: false
                                                                            };
                                                                        })
                                                                    );
                                                            }}
                                                            id="emitentPrehledVyjadreniBtn"
                                                        />
                                                    )}
                                                    {dokument.stav === EStavZadostiDok.HOTOVOIIS && (
                                                        <ButtonComponent
                                                            loading={loading.vyjadreni}
                                                            buttontext={'STAHNOUT'}
                                                            onClick={() => {
                                                                setLoading(pv => {
                                                                    return {
                                                                        ...pv,
                                                                        vyjadreni: true
                                                                    };
                                                                });
                                                                dokumentDownload(
                                                                    dokument.iddok,
                                                                    `${aktivniFirma?.ic} - ${getLanguageObject(language)[(dokument.typ === ETypDokumentu.PRIKAZY ? 'DOKUMENTY_PRIKAZ_' : 'TYP_SMLOUVY_') + dokument.podtyp]}.pdf`
                                                                ).finally(() =>
                                                                    setLoading(pv => {
                                                                        return {
                                                                            ...pv,
                                                                            vyjadreni: false
                                                                        };
                                                                    })
                                                                );
                                                            }}
                                                            id="emitentPrehledVyjadreniBtn"
                                                        />
                                                    )}
                                                </Row>
                                            </Col>
                                        );
                                    })}
                            </Row>
                            <Pagination
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end'
                                }}
                                total={prehled.count}
                                onChange={page => {
                                    setPagination(pv => {
                                        return {
                                            ...pv,
                                            offset: (page - 1) * 10
                                        };
                                    });
                                }}
                                pageSize={10}
                                current={prehled.offset / 10 + 1}
                                showSizeChanger={false}
                            />
                        </>
                    ) : aktivniFirma?.typ === EProfilyTyp.JINAOSOBA ? (
                        <Paragraph
                            style={{
                                display: 'block',
                                maxWidth: 800,
                                marginBottom: 10
                            }}
                            text="JINA_OSOBA_404"
                        />
                    ) : (
                        ''
                    )}
                </>
            )}
            <Modal
                title={<TranslateText>{'VYJADRENI_CDCP'}</TranslateText>}
                open={!!modalVyjadreni}
                onCancel={() => setModalVyjadreni('')}
                afterClose={() => {
                    setModalVyjadreni('');
                }}
                footer={null}
            >
                <Row>
                    <Paragraph text={modalVyjadreni} />
                </Row>
                <Row>
                    <ButtonComponent
                        style={{
                            marginTop: 20
                        }}
                        buttontext="ZAVRIT"
                        onClick={() => {
                            setModalVyjadreni('');
                        }}
                        id="emitentPrehledZavritBtn"
                    />
                </Row>
            </Modal>
        </LoadingWrapper>
    );
};

export default PrehledEmitent;
